import React, { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import { EhrConnectModal } from 'components/EhrConnect';
import { PATH } from 'constants/path';
import { trackHeapEvent } from 'utils/analytics';
import { saveBillingClaimScope } from 'api/insuranceBilling';
import { getCurrentUser } from 'api/common';
import { checkLeadFormByEmail } from 'api/ppaLeadForm';
import { getHomeInfo } from 'api/provider';
import useEhrConnections from 'hooks/useEhrConnections';
import type { InsuranceBillingOnboardingVo } from 'types/insuranceBilling';
import { fetchOnboardingVo } from 'pages/InsuranceBillingOnboarding/billingReviewData';
import { hasIntent, EHelpType } from 'pages/PPALeadFormPage/helpType';
import cheersImg from './imgs/cheers.png';
import s from './s.module.less';
import { EPPAStep } from 'types/ppaOnboarding';

const DOING_STEPS = [
    {
        title: 'Scanning your EHR',
        desc: 'Klarity is watching for billable encounters from today onward.',
    },
    {
        title: 'Validating against payer rules',
        desc: 'Every claim is checked for coding errors to ensure a high clean claim rate.',
    },
    {
        title: 'Awaiting your final review',
        desc: 'Claims will appear in your dashboard shortly. Nothing gets submitted without your approval.',
    },
];

// Insurance billing 激活成功页（原型 showOnboardingSuccess / showOnboardingPending），三个变体：
// active  - 选了受支持的 EHR：「now active」+ Klarity 正在做什么
// waiting - 只选了自建 EHR（isCustom）：等 Klarity 人工对接，「We are working on <name>」——
//           不能出 now active，没有任何东西在扫描，不能让用户等一张不会来的 claim
// noEhr   - 什么都没选（EHR 非必选）：「Connect an EHR to start billing」
export default function InsuranceBillingSubmittedPage() {
    const navigate = useNavigate();
    // undefined = 拉取中（渲染 Spin，避免闪错变体）；null = 拉取失败（按 active 兜底）
    const [vo, setVo] = useState<InsuranceBillingOnboardingVo | null | undefined>(undefined);
    const [connections, connectionsLoading] = useEhrConnections();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [showContinueOnboarding, setShowContinueOnboarding] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await fetchOnboardingVo();
            if (!cancelled) setVo(data);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userRes = await getCurrentUser();
                const email = userRes?.data?.data?.actingUser?.email;
                if (!email || cancelled) return;
                const leadRes = await checkLeadFormByEmail({ email });
                const practiceHelpType = leadRes?.data?.data?.leadForm?.practiceHelpType;
                if (cancelled) return;
                if (!hasIntent(practiceHelpType, EHelpType.PATIENT_GROWTH)) return;
                const homeRes = await getHomeInfo();
                if (cancelled) return;
                const ppaOnboardingFinish =
                    !!homeRes?.data?.data?.completedOnboarding ||
                    homeRes?.data?.data?.klarityOnboardingStep === EPPAStep.STEP_21;
                if (!ppaOnboardingFinish) {
                    setShowContinueOnboarding(true);
                }
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const customNames = useMemo(
        () => new Set(connections.filter((c) => c.isCustom).map((c) => c.ehrSystem)),
        [connections],
    );

    const billingEhrs = vo?.claimSettings?.billingEhrs || [];
    // 自建 EHR 在 billingEhrs 里与普通 EHR 混存，用 isCustom 连接记录区分
    const customSelected = billingEhrs.filter((n) => customNames.has(n));
    const supportedSelected = billingEhrs.filter((n) => !customNames.has(n));

    let variant: 'loading' | 'active' | 'waiting' | 'noEhr';
    if (vo === undefined || connectionsLoading) variant = 'loading';
    else if (!vo) variant = 'active';
    else if (billingEhrs.length === 0) variant = 'noEhr';
    else if (supportedSelected.length === 0) variant = 'waiting';
    else variant = 'active';

    // waiting 变体的自建 EHR 名（原型是单个 otherEhr；我们允许多个，串联展示）
    const customName = customSelected.join(', ');

    const goDashboard = () => {
        trackHeapEvent('WebApp - Insurance Billing Submitted - Click - Track Claims', {});
        navigate(`${PATH.DASHBOARD}/${PATH.INSURANCE_BILLING}`);
    };

    const handleContinueOnboarding = () => {
        trackHeapEvent('WebApp - Insurance Billing Submitted - Click - Continue Onboarding', {});
        navigate(PATH.PPA_ONBOARDING_STEPS);
    };

    // 从本页连上受支持的 EHR：并入 billingEhrs 保存（其余 claim-scope 字段沿用服务端已存值），
    // 本地同步 vo → 变体自动切到 active（此时确实开始扫描了）
    const handleEhrConnected = async (ehrName: string) => {
        setPickerOpen(false);
        const cs = vo?.claimSettings;
        const current = cs?.billingEhrs || [];
        const next = current.includes(ehrName) ? current : [...current, ehrName];
        if (next !== current) {
            try {
                await saveBillingClaimScope({
                    billingEhrs: next,
                    claimScope: cs?.claimScope ?? undefined,
                    claimStartMode: cs?.claimStartMode ?? undefined,
                    claimStartDate: cs?.claimStartDate ?? undefined,
                });
            } catch (e) {
                // 保存失败已由全局 toast 展示；连接本身已成功，展示仍按已连接推进
            }
        }
        setVo((prev) => (prev ? { ...prev, claimSettings: { ...prev.claimSettings, billingEhrs: next } } : prev));
    };

    // 三个非 active 文案位（原型 showOnboardingPending 的 waitingOnUs 两分支）
    const pendingTitle =
        variant === 'waiting'
            ? `Your setup is saved. We are working on ${customName}.`
            : 'Your setup is saved. Connect an EHR to start billing.';
    const pendingSteps = [
        {
            title: 'Your billing profile is complete',
            desc: 'Provider details, tax information, addresses, and your payout account are saved. Nothing to redo later.',
        },
        variant === 'waiting'
            ? {
                  title: `${customName} is on our list`,
                  desc: 'Our team will contact you about connecting it. You do not need to do anything in the meantime.',
              }
            : {
                  title: 'No EHR connected',
                  desc: 'Klarity reads appointments from your EHR. Connect one whenever you are ready, from Settings or the button below.',
              },
        {
            title: 'No claims yet',
            desc:
                variant === 'waiting'
                    ? `Klarity reads appointments from a connected EHR, so claims start once ${customName} is supported, or as soon as you connect one we already support.`
                    : 'Nothing is billed until an EHR is connected. Your setup stays saved until then.',
        },
    ];

    return (
        <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
            <div className={s.page}>
                <div className={s.topbar}>
                    <PPAOnboardingHeader showLogout />
                </div>
                {variant === 'loading' && (
                    <div className={s.loading}>
                        <Spin />
                    </div>
                )}
                {variant === 'active' && (
                    <div className={s.content}>
                        <div className={s.head}>
                            <img src={cheersImg} alt="" className={s.cheers} />
                            <h1 className={s.title}>Insurance claim submission is now active!</h1>
                        </div>
                        <div className={s.cols}>
                            <div>
                                <p className={s.intro}>Here is what Klarity is doing right now:</p>
                                {DOING_STEPS.map((item, i) => (
                                    <div key={item.title} className={s.step}>
                                        <span className={s.stepNum}>{i + 1}</span>
                                        <div>
                                            <div className={s.stepTitle}>{item.title}</div>
                                            <div className={s.stepDesc}>{item.desc}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div className={s.hbw}>
                                    <div className={s.hbwTitle}>How billing works</div>
                                    <p className={s.hbwBody}>
                                        Just 3.9% of successful reimbursements, half the industry average. You only pay
                                        after you get paid.
                                    </p>
                                    <p className={s.hbwNote}>
                                        Payers deposit each reimbursement directly into your payout account. Klarity
                                        then sends a monthly invoice for the 3.9% managed billing fee, which you pay
                                        after the reimbursements land.
                                    </p>
                                    <p className={s.hbwFootnote}>
                                        For Medicaid and Medicare claims, you&apos;ll be charged on a claim submission
                                        basis to be compliant.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={s.ctaRow}>
                            <Button type="primary" size="large" className={s.cta} onClick={goDashboard}>
                                Track my claims
                            </Button>
                            {showContinueOnboarding && (
                                <Button size="large" className={s.ctaSecondary} onClick={handleContinueOnboarding}>
                                    Continue onboarding
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                {(variant === 'noEhr' || variant === 'waiting') && (
                    <div className={s.content}>
                        <div className={s.head}>
                            <h1 className={s.title}>{pendingTitle}</h1>
                        </div>
                        <div className={s.cols}>
                            <div>
                                <p className={s.intro}>Where things stand:</p>
                                {pendingSteps.map((item, i) => (
                                    <div key={item.title} className={s.step}>
                                        <span className={s.stepNum}>{i + 1}</span>
                                        <div>
                                            <div className={s.stepTitle}>{item.title}</div>
                                            <div className={s.stepDesc}>{item.desc}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div className={s.hbw}>
                                    <div className={s.hbwTitle}>Want to start billing now?</div>
                                    <p className={s.hbwBody}>
                                        If your practice also uses Klarity Health, SimplePractice, Tebra, Athena,
                                        Healthie, IntakeQ, or Optimantra, connect it and Klarity can start on those
                                        patients today.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={s.ctaRow}>
                            <Button type="primary" size="large" className={s.cta} onClick={goDashboard}>
                                Go to Insurance billing
                            </Button>
                            <Button size="large" className={s.ctaSecondary} onClick={() => setPickerOpen(true)}>
                                Connect a supported EHR
                            </Button>
                            {showContinueOnboarding && (
                                <Button size="large" className={s.ctaSecondary} onClick={handleContinueOnboarding}>
                                    Continue onboarding
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                <EhrConnectModal
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    onConnected={handleEhrConnected}
                />
            </div>
        </ConfigProvider>
    );
}
